import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import LoginStyles from "../../Login/LoginStyles";
import { resetCommonSlice } from "../../store/common/CommonSlice";
import LoginWithNameAndMobile from "../../Login/LoginWithNameAndMobile/LoginWithNameAndMobile";
import LoginWithEmailOrMobile from "../../Login/LoginWithEmailOrMobile/LoginWithEmailOrMobile";
import { RootState } from "../../store/store";
import { IFeature } from "../../models/admin/features/features";
import RenderVideoBanner from "../../common/RenderVideoBanner/RenderVideoBanner";

interface IProps {
  logoSrc: string | File;
  loginBannerType: string | undefined;
  loginBannerSrc: string | File | undefined | null;
  isFunctionable: boolean;
}

const CustomerLoginPreview: React.FC<IProps> = ({
  logoSrc,
  loginBannerSrc,
  isFunctionable,
  loginBannerType,
}) => {
  const dispatch = useAppDispatch();
  const { features } = useAppSelector((state: RootState) => state.features);

  const [loginType, setLoginType] = useState<string>("");
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [bannerUrl, setBannerUrl] = useState<string>("");

  const handleLoginWithChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLoginType((event.target as HTMLInputElement).value);
      dispatch(resetCommonSlice());
    },
    [dispatch]
  );

  const loginOptions: IFeature[] = useMemo(
    () =>
      features.filter(
        (feature: any) =>
          (feature.featureCode === "name_mobile_login" && feature.selected) ||
          (feature.featureCode === "email_mobile_login" && feature.selected) ||
          (feature.featureCode === "google_login" && feature.selected)
      ),
    [features]
  );

  const getLoginForm = useCallback(() => {
    switch (loginType) {
      case "name_mobile_login":
        return <LoginWithNameAndMobile isFunctionable={isFunctionable} />;
      case "email_mobile_login":
        return <LoginWithEmailOrMobile isFunctionable={isFunctionable} />;
      default:
        return null;
    }
  }, [loginType, isFunctionable]);

  const handleImageSrc = useCallback((src: string | File) => {
    if (src instanceof File) {
      return URL.createObjectURL(src);
    } else if (typeof src === "string") {
      if (src.startsWith("data:image") || src.startsWith("http")) {
        return src;
      } else {
        return `data:image/png;base64,${src}`;
      }
    }
    return "";
  }, []);

  const logoUrlRef = React.useRef<string>("");
  const bannerUrlRef = React.useRef<string>("");

  useEffect(() => {
    logoUrlRef.current = handleImageSrc(logoSrc);
    setLogoUrl(logoUrlRef.current);
    return () => {
      if (logoSrc instanceof File && logoUrlRef.current) {
        URL.revokeObjectURL(logoUrlRef.current);
      }
    };
  }, [logoSrc, handleImageSrc]);

  useEffect(() => {
    if (loginBannerType === "image" && loginBannerSrc) {
      bannerUrlRef.current = handleImageSrc(loginBannerSrc);
      setBannerUrl(bannerUrlRef.current);
    } else if (loginBannerType === "video" && loginBannerSrc) {
      setBannerUrl(loginBannerSrc as string);
    }
    return () => {
      if (loginBannerSrc instanceof File && bannerUrlRef.current) {
        URL.revokeObjectURL(bannerUrlRef.current);
      }
    };
  }, [loginBannerSrc, handleImageSrc, loginBannerType]);

  useEffect(() => {
    if (features.length > 0) {
      const defaultLoginType = features.find(
        (feature: any) =>
          (feature.featureCode === "name_mobile_login" && feature.selected) ||
          (feature.featureCode === "email_mobile_login" && feature.selected) ||
          (feature.featureCode === "google_login" && feature.selected)
      )?.featureCode;

      setLoginType(defaultLoginType || "");
    }
  }, [features]);

  return (
    <Box
      sx={LoginStyles.login}
      id="customer_login_preview"
      data-test-id="customer_login_preview"
      aria-label="Customer Login Preview"
    >
      <Box
        sx={LoginStyles.loginImage}
        id="customer_login_banner"
        data-test-id="customer_login_banner"
        aria-label="Login Banner"
      >
        {loginBannerType === "image" && bannerUrl && (
          <img
            src={bannerUrl}
            alt="Login Banner Preview"
            id="customer_login_banner_image"
            data-test-id="customer_login_banner_image"
          />
        )}
        {loginBannerType === "video" && (
          <RenderVideoBanner videoUrl={bannerUrl as string} />
        )}
      </Box>
      <Box
        style={LoginStyles.loginCardContainer}
        id="customer_login_card"
        data-test-id="customer_login_card"
        aria-label="Login Card"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            width: "80%",
          }}
          id="customer_login_content"
          data-test-id="customer_login_content"
          aria-label="Login Content"
        >
          <Box
            sx={LoginStyles.logo}
            id="customer_login_logo"
            data-test-id="customer_login_logo"
            aria-label="Login Logo"
          >
            {logoUrl && (
              <img
                src={logoUrl}
                alt="Logo Preview"
                id="customer_login_logo_image"
                data-test-id="customer_login_logo_image"
              />
            )}
          </Box>
          <Typography
            sx={LoginStyles.loginMessage}
            id="customer_login_message"
            data-test-id="customer_login_message"
            aria-label="Welcome Message"
          >
            Welcome, please login to continue
          </Typography>
          <FormControl
            id="customer_login_form_control"
            data-test-id="customer_login_form_control"
            aria-label="Login Form Control"
          >
            {loginOptions.length > 0 && (
              <FormLabel
                sx={{
                  fontFamily: "Playfair Display",
                  fontSize: "20px",
                  fontWeight: "400",
                  lineHeight: "22px",
                  color: "rgba(56, 61, 78, 1)",
                }}
                id="customer_login_form_label"
                data-test-id="customer_login_form_label"
                aria-label="Login With"
              >
                Login With
              </FormLabel>
            )}
            <RadioGroup
              defaultValue="emailOrMobile"
              value={loginType}
              sx={{ display: "flex", flexDirection: "row" }}
              onChange={handleLoginWithChange}
              id="customer_login_radio_group"
              data-test-id="customer_login_radio_group"
              aria-label="Login Options"
            >
              {loginOptions.map((option) => (
                <FormControlLabel
                  key={option.featureCode}
                  value={option.featureCode}
                  control={<Radio />}
                  label={option.featureLabel}
                  disabled={!isFunctionable}
                  id={`customer_login_option_${option.featureCode}`}
                  data-test-id={`customer_login_option_${option.featureCode}`}
                  aria-label={`Login with ${option.featureLabel}`}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {getLoginForm()}
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerLoginPreview;
